import type { RevenueOverview } from '@qlk/shared';

import { dinhDangTien, KhoiMuc, Skeleton } from '../../../ui/index.js';
import { nhanKyNgan } from '../../../lib/ky.js';

/**
 * Biểu đồ cột chồng thực nhận theo tháng: phần kênh và phần nhạc.
 * Vẽ bằng CSS thuần, không thêm thư viện biểu đồ (giống module 01).
 */

function gonTien(giaTri: number): string {
  if (giaTri >= 1000) return `$${(giaTri / 1000).toFixed(1).replace('.', ',')}K`;
  return `$${String(Math.round(giaTri))}`;
}

export interface BieuDoThangProps {
  duLieu: RevenueOverview['monthly'];
  dangTai?: boolean;
  /**
   * `true` khi cột **chỉ có phần kênh** (người bị giới hạn theo kênh được
   * giao): bỏ chú giải "Nhạc" và bỏ cột "Nhạc" của bảng cho trình đọc màn
   * hình. Content ID kho nhạc là khoản của cả công ty, không chia theo kênh.
   *
   * Dữ liệu vào vẫn dùng chung kiểu: chỗ gọi đặt `musicNet = '0.00'` và
   * `total = channelNet`, nên phép tính chiều cao cột không đổi.
   */
  chiKenh?: boolean;
}

export function BieuDoThang({ duLieu, dangTai = false, chiKenh = false }: BieuDoThangProps) {
  const cao = Math.max(...duLieu.map((d) => Number(d.total)), 1);

  return (
    <KhoiMuc
      tieuDe={`Thực nhận ${String(duLieu.length)} tháng`}
      moTa={
        chiKenh
          ? 'USD · kênh bạn phụ trách, sau thuế Mỹ, Network và VAT'
          : 'USD · kênh sau Network + Content ID nhạc − phí kho nhạc'
      }
      phai={
        <ul className="flex items-center gap-4 text-phu text-chu-phu">
          <li className="flex items-center gap-2">
            <span className="inline-block size-3 rounded-[3px] bg-nhan" aria-hidden="true" />
            Kênh
          </li>
          {!chiKenh && (
            <li className="flex items-center gap-2">
              <span
                className="inline-block size-3 rounded-[3px]"
                style={{ backgroundColor: 'var(--color-do-nhac)' }}
                aria-hidden="true"
              />
              Nhạc
            </li>
          )}
        </ul>
      }
    >
      {dangTai ? (
        <Skeleton cao="240px" bo="vua" />
      ) : duLieu.length === 0 ? (
        <p className="py-10 text-center text-co-ban text-chu-phu">Chưa có dữ liệu tháng nào.</p>
      ) : (
        <div className="flex h-[260px] items-end justify-around gap-3 border-b border-vien">
          {duLieu.map((d) => {
            const tong = Number(d.total);
            const kenh = Number(d.channelNet);
            const chieuCao = Math.max(Math.round((tong / cao) * 200), 4);
            const phanKenh = tong > 0 ? Math.round((kenh / tong) * chieuCao) : 0;
            const phanNhac = Math.max(chieuCao - phanKenh, 0);

            return (
              <div key={d.period} className="flex min-w-0 flex-1 flex-col items-center gap-2">
                <span className="so-lieu text-phu font-semibold text-chu">{gonTien(tong)}</span>
                <div
                  className="flex w-full max-w-[56px] flex-col justify-end overflow-hidden rounded-t-[6px]"
                  style={{ height: `${String(chieuCao)}px` }}
                  title={
                    chiKenh
                      ? `${dinhDangTien(d.channelNet)} từ kênh`
                      : `${dinhDangTien(d.channelNet)} từ kênh · ${dinhDangTien(d.musicNet)} từ nhạc`
                  }
                >
                  <div
                    style={{
                      height: `${String(phanNhac)}px`,
                      backgroundColor: 'var(--color-do-nhac)',
                    }}
                    aria-hidden="true"
                  />
                  <div
                    className="bg-nhan"
                    style={{ height: `${String(phanKenh)}px` }}
                    aria-hidden="true"
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-2 flex justify-around gap-3">
        {duLieu.map((d) => (
          <span key={d.period} className="so-lieu flex-1 text-center text-phu text-chu-mo">
            {nhanKyNgan(d.period)}
          </span>
        ))}
      </div>

      {/* `sr-only` phải nằm trên `div`, không phải trên `<table>`: hộp bảng bỏ
          qua `height: 1px` và `overflow: hidden` nên bảng ẩn vẫn cao thật và
          đẩy tài liệu dài hơn khung app. Xem `e2e/93-mot-mo-hinh-cuon`. */}
      <div className="sr-only">
        <table>
          <caption>Thực nhận theo tháng</caption>
          <thead>
            <tr>
              <th scope="col">Kỳ</th>
              <th scope="col">Kênh</th>
              {!chiKenh && <th scope="col">Nhạc</th>}
              {!chiKenh && <th scope="col">Tổng</th>}
            </tr>
          </thead>
          <tbody>
            {duLieu.map((d) => (
              <tr key={d.period}>
                <td>{d.period}</td>
                <td>{d.channelNet}</td>
                {!chiKenh && <td>{d.musicNet}</td>}
                {!chiKenh && <td>{d.total}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </KhoiMuc>
  );
}
